/** /zh/comparisons/chatgpt — Chinese version. See ChatGpt.tsx for design notes. */
import { MarketingShell } from '../MarketingShell';

export function ChatGptZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/comparisons/chatgpt"
      documentTitle="给产品经理的 ChatGPT 替代方案：为什么它聊到后面会前后矛盾 · Draftlize"
      documentDescription="ChatGPT 很聪明，但在长项目里会前后矛盾——忘记定过的决策、拿过时假设往下推。看清原因，以及 Draftlize 如何让每个产品决策保持可寻址。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">对比 · vs ChatGPT</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            ChatGPT 很聪明，<br/>
            <em>但聊到后面总是前后矛盾。</em>
          </h1>
          <p className="lede">
            聊长了它就忘了你前面定的；同一个口径越聊越漂；你改了一个前提，它照样拿旧的往下推，从不提醒你。这不是模型不够聪明——换 Claude、换 Gemini 也一样。问题在「聊天」这种形态：它把你的思考存成一条会衰减、互不关联的消息流。Draftlize 把它换成另一种存法。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">问题不在 AI 笨，<em>在它把决策存在哪。</em></h2>
          </div>
          <p>ChatGPT 是个很好的聊天工具，这点没争议。但聊天是个糟糕的「决策存放处」——这才是它聊到后面前后不一致的真正根源。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>ChatGPT / Claude / Gemini</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>聊了好几周，还记得你定过的事</td><td className="ours yes">决策落成卡片，永久可查</td><td className="no">聊天历史会衰减</td></tr>
            <tr><td>改一处，自动标记受影响的地方</td><td className="ours yes">依赖图 + stale 标记</td><td className="no">拿旧前提往下推，不提醒</td></tr>
            <tr><td>自相矛盾，自动浮现</td><td className="ours yes">跨讨论冲突检测</td><td className="no">不会主动比对</td></tr>
            <tr><td>每个决策有固定位置可引用</td><td className="ours yes">卡片可寻址（URL / ID）</td><td className="no">埋在对话流里</td></tr>
            <tr><td>散落的想法，自动收口成方案</td><td className="ours yes">卡片图自动沉淀</td><td className="partial">留在聊天里，得自己捞</td></tr>
            <tr><td>开个新对话，上下文还在</td><td className="ours yes">卡片图就是上下文</td><td className="partial">memory 摘要，尽力而为</td></tr>
            <tr><td>Claude Code / Cursor 共用同一份</td><td className="ours yes">MCP + CLI</td><td className="no">各记各的</td></tr>
            <tr><td>一次性快速问答</td><td className="partial">大材小用</td><td className="yes">它的主场</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">ChatGPT <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">一次性问题</h3>
            <p>"总结这个 PDF。""写个 X 的 SQL。""翻译这个。"ChatGPT 很棒。Draftlize 对这些是大材小用——你不需要 substrate 给永不再引用的东西。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">语音 + 多模态 + 插件</h3>
            <p>语音对话、图像理解、插件生态——ChatGPT 做的这些都是实在有用的东西。Draftlize 是文本为主、聚焦在窄问题上。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">如果对话*就是*交付物</h3>
            <p>对那些对话本身就是工作的角色（客户支持草稿、销售外联、内容生成），聊天历史够用。Draftlize 是给那些对话在交付物*上游*的场景。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Draftlize <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">聊了好几周还不散架</h3>
            <p>你第 3 周定了定价，需要它在第 7 周的对话里仍然活着、随时能查。聊天记忆会衰减；卡片不会。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一处，下游自动跟着变</h3>
            <p>改了决策 A 让计划 B 失效时，你需要一个知道这层依赖的工具。ChatGPT 会乐呵呵拿过时的 A 生成 B，从不告诉你。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">多个 AI 工具，共用一份上下文</h3>
            <p>你用 Claude Code 实现、ChatGPT 头脑风暴、Claude Desktop 看资料——每个工具各记各的。Draftlize 卡片是任一工具通过 MCP 都能读写的同一份上下文。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          ChatGPT 是个不错的出声思考方式。Draftlize 是想法去落脚的地方。
        </blockquote>
        <cite>你可能两个都要。不是替代品——是层。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            一次性问答继续用 ChatGPT。加 Draftlize 给那些需要决策复利的多周产品工作。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
